import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

// bfcache (back/forward cache) restore: the browser reuses the DOM snapshot from before
// the navigation, so a product removed before going to /checkout reappears in the cart
// drawer and on the /cart page when the customer hits Back. cart-icon refreshes the count
// bubble on `pageshow`, but the rendered ROWS are stale until a cart event re-morphs them.
// On a persisted restore, re-render every cart-items-component section fresh (cache:false)
// so the drawer + /cart reflect the real cart. See [[cart-drawer-audit-findings]].
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
    const sectionId = component.dataset.sectionId;
    if (sectionId) {
      sectionRenderer.renderSection(sectionId, { cache: false, mode: 'full' });
    }
  });
});

/**
 * Vertical Oceans — 3-step cart drawer controller.
 *
 * Progressive enhancement for the drawer chrome rendered by snippets/cart-drawer.liquid
 * (Steps track, Step CTAs, free-ship bar, cart count). The real cart line items, totals,
 * and the Step 3 checkout recap live in the theme's <cart-items-component> /
 * cart-summary / vo-cart-checkout-recap (SRA-morphed) and are untouched here — the recap
 * is server-rendered and morphs inside its data-hydration-key node on every cart change.
 * This element only manages:
 *   - step state (1 Items → 2 Offer → 3 Checkout), step-dot nav, progress fill
 *   - the per-step CTA row (Continue / Back / Checkout). Checkout submits #cart-form.
 *   - syncing the chrome (count, free-ship bar) to the fresh cart on every
 *     shopify:cart:lines-update.
 *
 * It lives OUTSIDE the data-hydration-key morph nodes, so step state survives re-renders.
 *
 * Button wiring uses event delegation on the chrome root (so re-renders of the CTA row
 * never lose listeners), and a module-level document listener provides a reliable close
 * path for `.vo-cart-close` that also covers the standalone empty state (which renders no
 * <vo-cart-drawer> instance).
 */

// Module-level close fallback: works for every `.vo-cart-close` in the cart drawer,
// including the standalone empty state that has no <vo-cart-drawer> instance. This
// complements (does not replace) the theme `on:click="#cart-drawer/close"` attribute;
// ThemeDrawer.close() is a guarded no-op if already closing, so double-firing is safe.
document.addEventListener(
  'click',
  (event) => {
    const target = event.composedPath?.()[0] ?? event.target;
    if (!(target instanceof Element)) return;
    // Page dimming overlay (desktop squeeze mode) — click closes the cart drawer.
    if (target.closest('.vo-cart-overlay')) {
      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.close === 'function') drawer.close();
      return;
    }
    const closeBtn = target.closest('.vo-cart-close');
    if (!closeBtn) return;
    const drawer = closeBtn.closest('theme-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  },
  { capture: true }
);

class VoCartDrawer extends HTMLElement {
  #connected = false;
  /** @type {number} current step 1..3 */ #step = 1;
  /** @type {any | null} last-known cart */ #cart = null;
  /** @type {boolean} whether #cart holds a fresh /cart.js fetch (vs. a DOM-seeded stub) */ #hasFreshCart = false;
  /** @type {number} free-ship threshold in cents (0 = disabled) */ #thresholdCents = 0;
  /** @type {string} browse link for the empty-state CTA */ #browseLink = '';
  /** @type {HTMLElement | null} containing theme drawer (absent on the cart page) */ #themeDrawer = null;
  /** @type {boolean} reset the drawer to Step 1 before its next visible frame */ #resetOnNextOpen = false;

  connectedCallback() {
    if (this.#connected) return;
    this.#connected = true;

    this.#refreshDomRefs();
    // The cart PAGE renders the same <vo-cart-drawer> chrome but, unlike the
    // drawer, its section morphs in 'full' mode (no hydration nodes wrapping the
    // chrome), so the element can be recreated on a cart change and lose its
    // step. Persist the step to sessionStorage and restore on (re)connect.
    this.#isPage = this.#root?.dataset.voPage === 'true';

    // A dismissed drawer should always reopen at Items, even when it was closed
    // from Offer or Checkout. Reset on the next open event so the outgoing close
    // animation does not visibly jump back to Step 1.
    this.#themeDrawer = this.closest('theme-drawer');
    this.#themeDrawer?.addEventListener('theme-drawer:close', this.#onDrawerClose);
    this.#themeDrawer?.addEventListener('theme-drawer:open', this.#onDrawerOpen);

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);

    // Seed the count synchronously from the server-rendered pill so the CTA
    // appears instantly when the drawer opens — no /cart.js round-trip on the
    // critical path. The full cart is fetched in the background to refresh the
    // ship bar; until it resolves (#hasFreshCart = false) the server render is
    // authoritative and #syncChrome is a no-op.
    this.#seedCountFromDom();
    this.gotoStep(this.#initialStep());
    this.#seedCart().then(() => { this.#syncChrome(); this.#renderCta(); });
  }

  /**
   * Seed a minimal cart stub (item_count only) from the server-rendered count
   * pill, so #renderCta can render the right footer immediately without waiting
   * on a /cart.js fetch. Sets #hasFreshCart = false so #syncChrome skips
   * re-rendering the server-authored ship bar / count.
   */
  #seedCountFromDom() {
    const n = parseInt(this.#countEl?.textContent || '0', 10);
    this.#cart = {
      item_count: Number.isFinite(n) ? n : 0,
      items: [],
      attributes: {},
      items_subtotal_price: 0,
    };
    this.#hasFreshCart = false;
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#root?.removeEventListener('click', this.#onChromeClick);
    this.#themeDrawer?.removeEventListener('theme-drawer:close', this.#onDrawerClose);
    this.#themeDrawer?.removeEventListener('theme-drawer:open', this.#onDrawerOpen);
    this.#themeDrawer = null;
  }

  #onDrawerClose = () => {
    if (!this.#isPage) this.#resetOnNextOpen = true;
  };

  #onDrawerOpen = () => {
    if (this.#isPage || !this.#resetOnNextOpen) return;
    this.#resetOnNextOpen = false;
    this.gotoStep(1);
  };

  /**
   * Delegated click handler for Continue Shopping, step dots ([data-step]),
   * and step CTAs ([data-vo-goto]).
   * The checkout CTA carries no data-vo-goto and submits #cart-form naturally.
   * @param {Event} event
   */
  #onChromeClick = (event) => {
    const target = event.composedPath?.()[0] ?? event.target;
    if (!(target instanceof Element)) return;
    const continueShoppingBtn = target.closest('[data-vo-continue-shopping]');
    if (continueShoppingBtn) {
      event.preventDefault();
      event.stopPropagation();
      const drawer = this.closest('theme-drawer');
      if (drawer && typeof drawer.close === 'function') drawer.close();
      return;
    }
    const dot = target.closest('.vo-step-dot[data-step]');
    if (dot) {
      this.#gotoStepFromClick(event, Number(dot.dataset.step));
      return;
    }
    const goBtn = target.closest('[data-vo-goto]');
    if (goBtn) {
      this.#gotoStepFromClick(event, Number(goBtn.dataset.voGoto));
    }
  };

  /**
   * Navigate to a step in response to a chrome click (step dot or Continue/Back).
   *
   * Two independent guards prevent the drawer from closing on step-nav clicks:
   *  1. stopPropagation — the dialog's #onBackdropClick (bubble phase, on <dialog>)
   *     never sees the click, so it can't close.
   *  2. Deferring gotoStep() to a microtask — gotoStep() re-renders #ctaRow via
   *     innerHTML, detaching the clicked button. If the click still reached the
   *     backdrop handler (e.g. via a path we don't fully control), it would run
   *     with a detached target → panel.contains(target) is false →
   *     isClickedOutside() returns true → false close. Running gotoStep() AFTER
   *     the click finishes propagating keeps the target attached through the whole
   *     event, so contains() stays true even if the handler runs.
   * The microtask runs before the next paint, so there is no visible delay.
   * @param {Event} event
   * @param {number} step
   */
  #gotoStepFromClick(event, step) {
    event.stopPropagation();
    queueMicrotask(() => this.gotoStep(step));
  }

  /** @type {HTMLElement | null} */ #root = null;
  /** @type {HTMLElement[]} */ #dots = [];
  /** @type {HTMLElement[]} */ #panels = [];
  /** @type {HTMLElement | null} */ #ctaRow = null;
  /** @type {HTMLElement | null} */ #countEl = null;
  /** @type {HTMLElement | null} */ #shipBar = null;
  /** @type {HTMLElement | null} */ #notice = null;
  /** @type {number} */ #noticeTimer = 0;
  /** @type {boolean} */ #isPage = false;

  /** Re-query chrome after a full cart-section morph preserves this custom-element host
   *  but replaces its children. Also moves the delegated listener to the live root. */
  #refreshDomRefs() {
    const nextRoot = this.querySelector('[data-vo-cart]');
    if (nextRoot !== this.#root) {
      this.#root?.removeEventListener('click', this.#onChromeClick);
      this.#root = nextRoot;
      this.#root?.addEventListener('click', this.#onChromeClick);
    }
    this.#dots = Array.from(this.querySelectorAll('.vo-step-dot'));
    this.#panels = Array.from(this.querySelectorAll('.vo-cart-step'));
    this.#ctaRow = this.querySelector('[data-vo-cta-row]');
    this.#countEl = this.querySelector('[data-vo-cart-count]');
    this.#shipBar = this.querySelector('[data-vo-ship-bar]');
    this.#notice = this.querySelector('[data-vo-cart-notice]');
    this.#thresholdCents = Number(this.#shipBar?.dataset.thresholdCents || 0) || 0;
    this.#browseLink = this.#root?.dataset.voBrowseLink || this.#browseLink;
    this.#isPage = this.#root?.dataset.voPage === 'true';
  }

  async #seedCart() {
    try {
      const res = await fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin', cache: 'no-store' });
      this.#cart = await res.json();
      this.#hasFreshCart = true;
    } catch (error) {
      console.warn('[vo-cart-drawer] seed cart failed:', error);
    }
  }

  /** @param {Event & { promise?: Promise<any>, action?: string, lines?: any[] }} event */
  #onCartLinesUpdate = async (event) => {
    // Show a blur + spinner overlay on Step 1 for the duration of any cart line mutation —
    // add, remove, or quantity change — so removing/editing a product gets the same loading
    // feedback as adding one. The cart-items component dispatches this event at
    // mutation-start with a promise spanning the whole fetch + morph, so the overlay covers
    // exactly that window. Cleared in finally so it always comes off (even on abort/error).
    // The Step 2 offer add also matches, but its event fires only AFTER the add resolves
    // (vo-cart-offer.js drives the loader itself around the fetch), so this just clears it
    // on resolve — the offer's own finally is the backstop. Excluded: the offer's date-only
    // attribute "update" (action 'update' with no lines), which is not a line change and
    // must not blank Step 1 while the customer picks a delivery date.
    const action = event.action || '';
    const hasLines = (event.lines?.length || 0) > 0;
    const showLoader = action === 'add' || action === 'remove' || (action === 'update' && hasLines);
    if (showLoader) this.setLoading(true);
    // The offer controller tags its dispatched events with detail.source so we can
    // return to Step 1 + show a confirmation when a subscription is added from Step 2.
    let detail = null;
    try {
      const res = await event.promise;
      detail = res?.detail ?? null;
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[vo-cart-drawer] cart event rejected:', error);
    } finally {
      if (showLoader) this.setLoading(false);
    }
    // Cart morphs attached to the same promise may replace all chrome children while
    // retaining this <vo-cart-drawer> instance. Run after those handlers and rebind to
    // the live panels/CTA before rendering the post-update state.
    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.#refreshDomRefs();
    const source = detail?.source || '';

    // Fast path: the resolved detail already carries the raw /cart.js items.
    // Derive the absolute count from that complete list. product-form-component's
    // detail.itemCount is only the quantity added by the latest click, not the cart
    // total; using it made a two-product cart display 0 after subtracting the hidden
    // shipping-fee line.
    // Apply them now so the count pill + free-ship bar update without a second
    // /cart.js round-trip on the critical path — that extra serial fetch was the
    // cause of the noticeable lag when removing a product. #renderShipBar computes
    // the merchandise subtotal from the items directly, so this is also the correct
    // subtotal. The Step 3 checkout recap is server-rendered and morphed by the
    // theme's SRA flow (its data-hydration-key node), so nothing here touches it.
    if (detail?.items) {
      const itemCount = detail.items.reduce(
        (sum, item) => sum + (Number(item?.quantity) || 0),
        0,
      );
      this.#cart = {
        ...(this.#cart || {}),
        items: detail.items,
        item_count: itemCount,
      };
      this.#hasFreshCart = true;
    } else {
      // Older/other dispatcher with no items in the event — fall back to a fetch.
      await this.#seedCart();
    }

    if (this.#merchCount() === 0) {
      this.gotoStep(1);
    } else if (source === 'vo-cart-offer') {
      // A subscription was just added from Step 2 — bring the customer back to Items
      // and confirm above the free-shipping bar. Driven here (not from the offer
      // element) so it fires whenever the add succeeds, regardless of upgrade timing.
      this.gotoStep(1);
      this.showNotice('Subscription added to your cart');
    } else {
      // The cart page morphs in 'full' mode, re-rendering the chrome from server
      // HTML (step 1 active). Re-apply the current step on the next frame — after
      // any morph completes but before paint — so the user's step is restored with
      // no step-1 flash. No-op on the drawer (hydration morph leaves panels intact).
      requestAnimationFrame(() => this.gotoStep(this.#step));
    }
    this.#syncChrome();
    // Re-render the CTA so the footer reacts to empty vs filled (Continue/Checkout).
    this.#renderCta();
  };

  // ---- Steps ----
  /**
   * Show a transient confirmation notice above the free-shipping bar (e.g. after
   * adding a subscription from Step 2). Auto-hides after a few seconds. Public so
   * sibling chrome (e.g. <vo-cart-offer>) can call it: `drawer.showNotice(msg)`.
   * @param {string} message
   */
  showNotice(message) {
    const notice = this.#notice || this.querySelector('[data-vo-cart-notice]');
    if (!notice) return;
    notice.textContent = message;
    notice.hidden = false;
    if (this.#noticeTimer) clearTimeout(this.#noticeTimer);
    this.#noticeTimer = setTimeout(() => {
      notice.hidden = true;
      this.#noticeTimer = 0;
    }, 4000);
  }
  /**
   * Toggle a blur + spinner loading overlay on the Step 1 panel, shown while a cart line
   * mutation is in flight (add / remove / quantity change). Public so <vo-cart-offer> can
   * drive it for the Step 2 add: that add's CartLinesUpdateEvent fires only AFTER
   * /cart/add resolves, so the drawer's own listener can't span the fetch — the offer calls
   * this directly before the fetch and the drawer clears it when the add resolves (the
   * offer's finally is a backstop). The class sits on the panel, which is outside the
   * hydration-key morph nodes, so it survives the SRA re-render that brings the new item
   * in. No-op if Step 1 isn't present.
   * @param {boolean} on
   */
  setLoading(on) {
    const panel = this.querySelector('[data-panel="1"]');
    if (!panel) return;
    panel.classList.toggle('is-loading', !!on);
    panel.setAttribute('aria-busy', on ? 'true' : 'false');
  }
  gotoStep(n) {
    this.#step = Math.max(1, Math.min(3, n));
    this.#panels.forEach((p) => p.classList.toggle('is-active', Number(p.dataset.panel) === this.#step));
    this.#dots.forEach((d) => {
      const s = Number(d.dataset.step);
      d.classList.toggle('active', s === this.#step);
      d.classList.toggle('done', s < this.#step);
      const circle = d.querySelector('.circle');
      if (circle) {
        circle.innerHTML =
          s < this.#step
            ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 13l4 4L19 7"/></svg>'
            : String(s);
      }
    });
    const fill = this.querySelector('.vo-steps-line .fill');
    if (fill) fill.style.width = `${((this.#step - 1) / 2) * 100}%`;
    this.#root?.setAttribute('data-step', String(this.#step));
    this.#renderCta();
    this.#syncChrome();
    if (this.#isPage) this.#saveStep(this.#step);
  }

  /** Initial step on connect: 1 for the drawer, the persisted step for the page. */
  #initialStep() {
    if (!this.#isPage) return 1;
    // sessionStorage may be unavailable (private mode, blocked storage) — same guard as
    // #saveStep. A throw here would abort connectedCallback before gotoStep runs, leaving
    // the cart page's drawer un-rendered, so fall back to Step 1 instead of crashing.
    let saved = NaN;
    try {
      saved = Number(sessionStorage.getItem('vo-cart-page-step'));
    } catch {
      /* sessionStorage unavailable — non-fatal, fall back to Step 1. */
    }
    return Number.isFinite(saved) && saved >= 1 && saved <= 3 ? saved : 1;
  }

  /** @param {number} step */
  #saveStep(step) {
    try {
      sessionStorage.setItem('vo-cart-page-step', String(step));
    } catch {
      /* sessionStorage may be unavailable (private mode) — non-fatal. */
    }
  }

  /** Merchandise item count EXCLUDING the hidden `_shipping_fee` line, so empty-state
   *  and minimum-gate decisions treat a fee-only cart (the race window after removing
   *  the last product, before the fee engine re-syncs) as empty — not as a 1-item cart
   *  the customer can check out. For the DOM-seeded stub cart (items: []) the pill's
   *  item_count is already fee-excluded, so this is correct for both the stub and a
   *  fresh /cart.js fetch (where item_count includes the fee line). */
  #merchCount() {
    const feeQty = (this.#cart?.items ?? []).reduce(
      (s, i) => (i.properties && i.properties._shipping_fee === 'true' ? s + (i.quantity || 0) : s),
      0,
    );
    return Math.max(0, (this.#cart?.item_count ?? 0) - feeQty);
  }

  #renderCta() {
    if (!this.#ctaRow) return;
    const empty = this.#merchCount() === 0;
    const arrow = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
    // Minimum-order gate: every initial delivery date and recurring subscription
    // schedule must avoid a lone minimum-variant unit (e.g. a single 500g).
    // Consulted here on every cart change so all step CTAs stay in sync.
    const gate = window.VOMinimumGate || null;
    const blocked = !!(gate && gate.isBlocked(this.#cart));
    const msgs = (gate && gate.messages) || {};
    const minMsg = blocked
      ? `<p class="vo-minimum-msg" role="alert">${msgs.blockedMessage || ''}</p>`
      : '';
    const continueShopping = this.#isPage
      ? `<a class="vo-cta ghost" href="${this.#browseLink || '#'}">Continue Shopping</a>`
      : '<button class="vo-cta ghost" type="button" data-vo-continue-shopping>Continue Shopping</button>';
    // Empty cart (no merchandise) → show Continue Shopping on ANY step, not just
    // Step 1. A fee-only cart can render on a persisted Step 3 (cart page); it must
    // never show an enabled Checkout CTA.
    if (empty) {
      this.#ctaRow.innerHTML = continueShopping;
      return;
    }
    if (this.#step === 1) {
      this.#ctaRow.innerHTML = this.#isPage
        ? `${minMsg}<button class="vo-cta" type="button" data-vo-goto="2">Continue ${arrow}</button>`
        : `${minMsg}${continueShopping}<button class="vo-cta" type="button" data-vo-goto="2">Continue ${arrow}</button>`;
    } else if (this.#step === 2) {
      // When blocked, disable Continue to step 3 and show the message — the
      // one-time upsell cards on this step let the customer add a companion.
      const continueAttrs = blocked ? ' disabled aria-disabled="true"' : '';
      const secondaryAction = this.#isPage
        ? '<button class="vo-back-link" type="button" data-vo-goto="1">‹ Back</button>'
        : continueShopping;
      this.#ctaRow.innerHTML = `${minMsg}${secondaryAction}<button class="vo-cta" type="button" data-vo-goto="3"${continueAttrs}>Continue ${arrow}</button>`;
    } else {
      // Step 3 checkout submit. When blocked, disable it + offer a way back to the
      // offer step (data-vo-goto is handled by the delegated #onChromeClick listener).
      if (blocked) {
        const secondaryAction = this.#isPage
          ? (msgs.browseAddons ? `<button class="vo-back-link" type="button" data-vo-goto="2">‹ ${msgs.browseAddons}</button>` : '<button class="vo-back-link" type="button" data-vo-goto="2">‹ Back</button>')
          : continueShopping;
        this.#ctaRow.innerHTML = `${minMsg}${secondaryAction}<button class="vo-cta vo-minimum-blocked" type="submit" form="cart-form" name="checkout" disabled aria-disabled="true">Checkout ${arrow}</button>`;
      } else {
        const secondaryAction = this.#isPage
          ? '<button class="vo-back-link" type="button" data-vo-goto="2">‹ Back</button>'
          : continueShopping;
        this.#ctaRow.innerHTML = `${secondaryAction}<button class="vo-cta" type="submit" form="cart-form" name="checkout">Checkout ${arrow}</button>`;
      }
    }
    // Step-nav buttons are handled by the delegated #onChromeClick listener on
    // the chrome root, so no per-button listeners are needed here.
  }

  // ---- Chrome sync (count, ship bar, checkout recap) ----
  #syncChrome() {
    // Until a fresh /cart.js fetch resolves, the server render is authoritative
    // — don't clobber it with a DOM-seeded stub. See #seedCountFromDom.
    if (!this.#hasFreshCart) return;

    // Cart count — exclude the hidden "Shipping Fee" line item (properties._shipping_fee)
    // so the pill shows real products only. Same basis as #renderCta's empty check.
    const count = this.#merchCount();
    if (this.#countEl) {
      const next = String(count);
      if (this.#countEl.textContent !== next) {
        this.#countEl.textContent = next;
        // Pulse the pill so the change is perceptible. Restart the animation
        // by toggling the class with a reflow in between.
        this.#countEl.classList.remove('pulse');
        void this.#countEl.offsetWidth;
        this.#countEl.classList.add('pulse');
      }
    }

    // Free-ship bar
    this.#renderShipBar();
  }

  #renderShipBar() {
    // Re-query each render so a stale (morph-replaced) reference never writes to
    // a detached node, and re-read the threshold from the live element.
    const shipBar = this.querySelector('[data-vo-ship-bar]');
    if (!shipBar) return;
    const thresholdCents = Number(shipBar.dataset.thresholdCents || 0);
    if (!thresholdCents) return;

    const items = this.#cart?.items ?? [];
    // Merchandise subtotal = sum of real (non-fee) line totals, computed from the
    // items so it's consistent whether the cart came from a full /cart.js fetch
    // (which carries items_subtotal_price) OR a cart-update event's detail.items
    // (which carries items but NOT items_subtotal_price). line_price IS the unit
    // price × quantity (the pre-line-discount total), so summing the non-fee lines
    // reproduces items_subtotal_price − fee. line_price is omitted in some fixtures,
    // so fall back to price × quantity. Excluding the hidden "Shipping Fee" line
    // item also keeps the fee itself from counting toward the free-shipping
    // threshold — otherwise adding a dated product (which makes the fee engine add
    // the fee item) would inflate the subtotal and falsely unlock free shipping.
    const subtotal = items.reduce(
      (s, i) =>
        i.properties && i.properties._shipping_fee === 'true'
          ? s
          : s + (i.line_price ?? (i.price || 0) * (i.quantity || 0)),
      0
    );
    const unlocked = subtotal >= thresholdCents;
    shipBar.classList.toggle('unlocked', unlocked);
    if (unlocked) {
      shipBar.innerHTML = `
        <div class="msg done"><span>You've unlocked free delivery</span><span class="amt">✓</span></div>
        <div class="track"><div class="fill" style="width:100%"></div></div>`;
    } else {
      const remaining = Math.max(0, thresholdCents - subtotal);
      const pct = Math.min(100, (subtotal / thresholdCents) * 100);
      shipBar.innerHTML = `
        <div class="msg"><span>You're <span class="amt">${this.#money(remaining)}</span> away from free delivery</span><span>${this.#money(thresholdCents)}</span></div>
        <div class="track"><div class="fill" style="width:${pct}%"></div></div>`;
    }
  }

  /** @param {number} cents */
  #money(cents) {
    const currency = this.#cart?.currency || '';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || undefined }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }
}

if (!customElements.get('vo-cart-drawer')) {
  customElements.define('vo-cart-drawer', VoCartDrawer);
}
